/**
 * API Client for Financial Metrics
 * Handles HTTP requests to backend Express API
 */

import axios, { AxiosError } from 'axios';
import type { MetricsSummary, CashFlowForecast, ApiError } from '@/types/metrics';
import type { ClientInvoicesResponse, BankTransactionsResponse } from '@/types/invoices';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Fetch all financial metrics from backend
 */
export async function fetchMetrics(): Promise<MetricsSummary> {
  try {
    const response = await apiClient.get<{ data: MetricsSummary }>('/metrics');
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.data) {
        throw new Error(
          axiosError.response.data.message || 'Failed to fetch metrics'
        );
      }
      if (axiosError.code === 'ECONNABORTED') {
        throw new Error('Request timeout - please try again');
      }
      if (!axiosError.response) {
        throw new Error(
          'Unable to connect to server - please check your connection'
        );
      }
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * Fetch invoices with arbitrary query params (used by drill-down modals)
 */
export async function fetchInvoices(
  params: Record<string, string>
): Promise<ClientInvoicesResponse> {
  try {
    const searchParams = new URLSearchParams(params);
    const response = await apiClient.get<ClientInvoicesResponse>(
      `/invoices?${searchParams.toString()}`
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.data) {
        throw new Error(
          axiosError.response.data.message || 'Failed to fetch invoices'
        );
      }
      if (axiosError.code === 'ECONNABORTED') {
        throw new Error('Request timeout - please try again');
      }
      if (!axiosError.response) {
        throw new Error(
          'Unable to connect to server - please check your connection'
        );
      }
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * Fetch invoices for a specific client (used by drill-down modal)
 * Matches filterSalesInvoices() criteria: ACCREC, non-VOIDED/DRAFT/DELETED
 */
export async function fetchClientInvoices(
  contactId: string
): Promise<ClientInvoicesResponse> {
  return fetchInvoices({
    contactID: contactId,
    type: 'ACCREC',
    status: 'SUBMITTED,AUTHORISED,PAID',
    pageSize: '100',
  });
}

/**
 * Fetch bank transactions with optional filters
 */
export async function fetchBankTransactions(
  params: Record<string, string> = {}
): Promise<BankTransactionsResponse> {
  try {
    const searchParams = new URLSearchParams(params);
    const url = Object.keys(params).length > 0
      ? `/bank-transactions?${searchParams.toString()}`
      : '/bank-transactions';
    const response = await apiClient.get<BankTransactionsResponse>(url);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.data) {
        throw new Error(
          axiosError.response.data.message || 'Failed to fetch bank transactions'
        );
      }
      if (axiosError.code === 'ECONNABORTED') {
        throw new Error('Request timeout - please try again');
      }
      if (!axiosError.response) {
        throw new Error(
          'Unable to connect to server - please check your connection'
        );
      }
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * Fetch reconciliation suggestions
 */
export async function fetchReconciliationSuggestions(): Promise<ReconciliationSuggestionsResponse> {
  try {
    const response = await apiClient.get<ReconciliationSuggestionsResponse>(
      '/reconciliation/suggestions'
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.data) {
        throw new Error(
          axiosError.response.data.message || 'Failed to fetch suggestions'
        );
      }
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * Apply a reconciliation match (create payment in Xero)
 */
export async function applyReconciliation(
  request: ReconciliationApplyRequest
): Promise<{ success: boolean }> {
  try {
    const response = await apiClient.post<{ success: boolean }>(
      '/reconciliation/apply',
      request
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.data) {
        throw new Error(
          axiosError.response.data.message || 'Failed to apply reconciliation'
        );
      }
    }
    throw new Error('An unexpected error occurred');
  }
}

export interface ReconciliationMatch {
  bankTransactionID: string;
  invoiceID: string;
  invoiceNumber: string;
  bankReference: string;
  bankAmount: number;
  invoiceAmount: number;
  contactName: string;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
}

export interface ReconciliationSuggestionsResponse {
  data: ReconciliationMatch[];
}

export interface ReconciliationApplyRequest {
  invoiceID: string;
  bankTransactionID: string;
  amount: number;
  date: string;
}

/**
 * Fetch detailed cash flow forecast with per-invoice breakdown
 */
export async function fetchDetailedCashflow(): Promise<CashFlowForecast> {
  try {
    const response = await apiClient.get<{ data: CashFlowForecast }>(
      '/metrics/cashflow?detailed=true'
    );
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.data) {
        throw new Error(
          axiosError.response.data.message || 'Failed to fetch forecast'
        );
      }
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * Health check endpoint
 */
export async function checkApiHealth(): Promise<boolean> {
  try {
    const response = await apiClient.get('/health');
    return response.status === 200;
  } catch {
    return false;
  }
}
